import {
  Badge,
  Button,
  Checkbox,
  cn,
  Flex,
  Icon,
  Input,
  Label,
  Markdown,
  Text,
} from '@kognitos/lattice';
import { useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';

import type {
  V1InterruptQuestion,
  V1QuestionAnswer,
} from '@/generated/hooks/rain/models';
import { INTERRUPT_OTHER_PREFIX } from '../constants';
import {
  parseInterruptOptionLabel,
  summarizeInterruptAnswer,
} from '../utils/interrupt-option';

export interface IRainInterruptCardProps {
  interruptId: string;
  questions: V1InterruptQuestion[];
  answered: boolean;
  answers?: V1QuestionAnswer[];
  isSubmitting?: boolean;
  onSubmit?: (answers: V1QuestionAnswer[]) => void;
  className?: string;
}

interface IQuestionState {
  selected: Set<string>;
  customText: string;
  customSelected: boolean;
}

const createEmptyQuestionState = (): IQuestionState => ({
  selected: new Set<string>(),
  customText: '',
  customSelected: false,
});

const buildInitialState = (
  questions: V1InterruptQuestion[],
  answered: boolean,
  answers?: V1QuestionAnswer[],
): Map<string, IQuestionState> => {
  const answerByQuestion = new Map<string, string[]>(
    (answers ?? []).map(a => [a.question_id ?? '', a.selected ?? []]),
  );
  const initial = new Map<string, IQuestionState>();
  for (const question of questions) {
    const questionId = question.id ?? '';
    const selected = new Set<string>();
    let customText = '';
    let customSelected = false;
    // When the card mounts already answered (e.g. history replay), hydrate the
    // selection from the prior answers so the frozen card reflects what was
    // picked. Live submissions keep their in-progress local state instead.
    if (answered) {
      for (const value of answerByQuestion.get(questionId) ?? []) {
        if (value.startsWith(INTERRUPT_OTHER_PREFIX)) {
          customText = value.slice(INTERRUPT_OTHER_PREFIX.length);
          customSelected = true;
        } else {
          selected.add(value);
        }
      }
    }
    initial.set(questionId, { selected, customText, customSelected });
  }
  return initial;
};

const hasAnswer = (state: IQuestionState | undefined): boolean => {
  if (!state) return false;
  if (state.selected.size > 0) return true;
  return state.customSelected && state.customText.trim().length > 0;
};

const buildAnswerPayload = (
  questions: V1InterruptQuestion[],
  questionState: Map<string, IQuestionState>,
): V1QuestionAnswer[] =>
  questions.map(question => {
    const questionId = question.id ?? '';
    const state = questionState.get(questionId);
    const selected: string[] = [];
    if (state) {
      selected.push(...Array.from(state.selected));
      if (state.customSelected) {
        const custom = state.customText.trim();
        if (custom) selected.push(`${INTERRUPT_OTHER_PREFIX}${custom}`);
      }
    }
    return { question_id: questionId, selected };
  });

export function RainInterruptCard({
  interruptId,
  questions,
  answered,
  answers,
  isSubmitting = false,
  onSubmit,
  className,
}: IRainInterruptCardProps) {
  const { t } = useTranslation('rain');
  const [questionState, setQuestionState] = useState(() =>
    buildInitialState(questions, answered, answers),
  );
  const [currentIndex, setCurrentIndex] = useState(0);

  const totalQuestions = questions.length;
  const safeIndex = Math.min(currentIndex, Math.max(totalQuestions - 1, 0));
  const isLastQuestion = safeIndex === totalQuestions - 1;

  /**
   * The only way forward: move to the next question, or submit once there isn't
   * one. Everything that moves the card on — picking a single-select option,
   * Enter on the "Other" field, the →/Submit button, Skip, and the header's
   * next chevron — calls this.
   *
   * Unanswered questions need no special handling: `buildAnswerPayload` emits an
   * empty `selected` for anything untouched, so submitting auto-skips whatever
   * the user never answered.
   *
   * `state` defaults to the current selections; callers that move forward *by*
   * changing a selection pass their freshly computed map, since `questionState`
   * is still the pre-change value while their handler runs.
   */
  const forward = (state: Map<string, IQuestionState> = questionState) => {
    // A reply is already in flight: the parent has the answers and is waiting on
    // rain. Guarded here rather than at each call site so the invariant lives
    // with the rule — the card must never submit twice for one interrupt.
    if (isSubmitting) return;
    if (!isLastQuestion) {
      setCurrentIndex(idx => idx + 1);
      return;
    }
    onSubmit?.(buildAnswerPayload(questions, state));
  };

  const back = () => {
    setCurrentIndex(idx => Math.max(idx - 1, 0));
  };

  /**
   * Applies `update` to one question's selections. Pass `movesForward` when the
   * change is the user's final act on this question, so the card advances (or
   * submits) off the freshly computed state.
   */
  const updateQuestion = (
    questionId: string,
    update: (prev: IQuestionState) => IQuestionState,
    movesForward = false,
  ) => {
    const next = new Map(questionState);
    next.set(
      questionId,
      update(questionState.get(questionId) ?? createEmptyQuestionState()),
    );
    setQuestionState(next);
    if (movesForward) forward(next);
  };

  const handleToggle = (
    questionId: string,
    optionLabel: string,
    multiSelect: boolean,
  ) => {
    updateQuestion(
      questionId,
      prev => {
        const selected = new Set(prev.selected);
        if (multiSelect) {
          if (selected.has(optionLabel)) selected.delete(optionLabel);
          else selected.add(optionLabel);
          return { ...prev, selected };
        }
        selected.clear();
        selected.add(optionLabel);
        return { ...prev, selected, customSelected: false, customText: '' };
      },
      // A single-select pick is a complete answer on its own. Multi-select needs
      // the button, since the user may still be adding options.
      !multiSelect,
    );
  };

  const handleToggleCustom = (questionId: string, multiSelect: boolean) => {
    updateQuestion(questionId, prev => {
      if (multiSelect) {
        return { ...prev, customSelected: !prev.customSelected };
      }
      const turnOn = !prev.customSelected;
      return {
        ...prev,
        customSelected: turnOn,
        selected: turnOn ? new Set<string>() : prev.selected,
        customText: turnOn ? prev.customText : '',
      };
    });
  };

  const handleCustomText = (
    questionId: string,
    multiSelect: boolean,
    text: string,
  ) => {
    updateQuestion(questionId, prev => {
      if (!multiSelect && text.length > 0) {
        return {
          ...prev,
          customText: text,
          customSelected: true,
          selected: new Set<string>(),
        };
      }
      return {
        ...prev,
        customText: text,
        customSelected: prev.customSelected || text.length > 0,
      };
    });
  };

  // Once answered, the card collapses to a plain read-only "question above,
  // answer below" list for every question at once (no more per-option rows
  // or prev/next pagination) — see Figma "Chat - Submitted Single Question"
  // (node 233:14201). This mirrors how Claude Code itself renders an
  // answered question, and sidesteps the old frozen-option-list rendering
  // entirely, so everything below this branch only ever runs unanswered.
  if (answered) {
    return (
      <Flex
        direction="column"
        gap={4}
        className={cn(
          'w-full min-w-0 max-w-full rounded-lg border border-border bg-card p-4',
          className,
        )}
        data-testid="rain-interrupt-card"
        data-interrupt-id={interruptId}
        data-answered="true"
      >
        {questions.map(question => {
          const qId = question.id ?? '';
          const selected =
            answers?.find(a => a.question_id === qId)?.selected ?? [];
          const summary = summarizeInterruptAnswer(selected);
          return (
            <Flex
              key={qId}
              direction="column"
              gap={0.5}
              className="w-full min-w-0"
              data-testid="rain-interrupt-answer"
            >
              {/* Markdown, not plain text: agents write questions containing
                  backticked step/field names and other inline formatting. */}
              <Markdown
                size="sm"
                textProps={{ level: 'small' }}
                textClassName="break-anywhere text-card-foreground"
              >
                {question.question ?? ''}
              </Markdown>
              {summary.skipped ? (
                <Text as="span" level="small" color="muted" className="italic">
                  {t('rain.interrupt.skipped')}
                </Text>
              ) : (
                <Flex
                  direction="row"
                  align="start"
                  gap={1}
                  className="w-full min-w-0"
                >
                  {summary.isCustom && (
                    <Icon
                      type="TextCursorInput"
                      size="sm"
                      className="mt-0.5 text-muted-foreground shrink-0"
                    />
                  )}
                  {/* Long answers wrap onto the next line rather than being
                      ellipsised; min-w-0 keeps the flex item from pushing the
                      card wider than the chat column. */}
                  <Text
                    as="span"
                    level="small"
                    color="muted"
                    className="min-w-0 break-anywhere"
                  >
                    {summary.hasRecommended
                      ? t('rain.interrupt.answerRecommended', {
                          answer: summary.text,
                        })
                      : summary.text}
                  </Text>
                </Flex>
              )}
            </Flex>
          );
        })}
      </Flex>
    );
  }

  // Below here `answered` is always false — the card is interactive.
  const interactionDisabled = isSubmitting;

  const currentQuestion = questions[safeIndex];
  const questionId = currentQuestion?.id ?? '';
  const state = questionState.get(questionId);
  const multiSelect = currentQuestion?.multi_select ?? false;
  const currentAnswered = hasAnswer(state);
  const options = currentQuestion?.options ?? [];

  /**
   * Whether the current question needs an explicit commit control at all.
   *
   * Single-select doesn't: picking an option moves the card on by itself, and
   * Enter commits a typed "Other" answer — so the button would only ever
   * duplicate paths the user already has. The exception is a typed answer
   * (`currentAnswered` can only be true here once text is in the "Other" field,
   * since a picked option would have advanced already), which needs a way to be
   * committed by mouse. Multi-select always needs it: ticking deliberately does
   * not advance.
   *
   * Rendering it is separate from enabling it — see the button's `disabled`. For
   * multi-select that means visible-but-disabled until the first tick; for
   * single-select the two conditions coincide, so it is never shown disabled.
   */
  const showForwardButton = multiSelect || currentAnswered;

  // Skip clears this question before moving on. Without the clear it would
  // submit whatever sits in the "Other" field — typing populates the selection
  // on every keystroke — so the label would be a lie.
  const skip = () => updateQuestion(questionId, createEmptyQuestionState, true);

  // Enter commits a typed "Other" answer. Multi-select is excluded: the user may
  // still be ticking options, so only the button finalises there.
  const handleCustomKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter' || multiSelect || interactionDisabled) return;
    if (!currentAnswered) return;
    event.preventDefault();
    forward();
  };

  return (
    <Flex
      direction="column"
      gap={3}
      className={cn(
        'border border-border bg-secondary rounded-xl pt-5 pb-4 w-full min-w-0 max-w-full',
        className,
      )}
      data-testid="rain-interrupt-card"
      data-interrupt-id={interruptId}
      data-answered="false"
    >
      <Flex direction="column" gap={2} className="px-4 w-full min-w-0">
        {totalQuestions > 1 && (
          <Flex
            direction="row"
            align="center"
            gap={1}
            justify="end"
            width="full"
          >
            {safeIndex > 0 && (
              <Button
                variant="ghost"
                size="icon-sm"
                disabled={isSubmitting}
                onClick={back}
                aria-label={t('rain.interrupt.previous')}
                data-testid="rain-interrupt-prev"
              >
                <Icon type="ChevronLeft" size="sm" />
              </Button>
            )}
            <Text level="small" color="muted">
              {t('rain.interrupt.pagination', {
                current: safeIndex + 1,
                total: totalQuestions,
              })}
            </Text>
            <Button
              variant="ghost"
              size="icon-sm"
              // Disabled on the last question, so this navigation control can
              // never reach `forward`'s submit branch.
              disabled={isLastQuestion || isSubmitting}
              onClick={() => forward()}
              aria-label={t('rain.interrupt.next')}
              data-testid="rain-interrupt-next"
            >
              <Icon type="ChevronRight" size="sm" />
            </Button>
          </Flex>
        )}
        <Flex direction="row" align="start" gap={3} width="full">
          <Icon
            type="CircleHelp"
            size="md"
            className="text-card-foreground shrink-0"
            data-testid="rain-interrupt-question-icon"
          />
          {/* aria-live so a screen reader announces the new question when
            answering advances the card, rather than silently swapping it.
            Markdown, not plain text: agents write questions containing
            backticked step/field names and other inline formatting. */}
          <Flex
            direction="column"
            className="min-w-0 flex-1"
            aria-live="polite"
          >
            <Markdown
              size="sm"
              textProps={{ weight: 'medium' }}
              textClassName="break-anywhere text-base leading-6 tracking-[0.16px] text-card-foreground"
            >
              {currentQuestion?.question ?? ''}
            </Markdown>
          </Flex>
        </Flex>
      </Flex>

      <Flex direction="column" gap={0} className="px-3 w-full min-w-0">
        {options.map((option, i) => {
          const label = option.label ?? '';
          const { label: displayLabel, isRecommended } =
            parseInterruptOptionLabel(label);
          const checked = state?.selected.has(label) ?? false;
          const onActivate = () => {
            if (interactionDisabled) return;
            handleToggle(questionId, label, multiSelect);
          };
          return (
            <div
              // eslint-disable-next-line react-x/no-array-index-key -- reason: keyed by position, not label, so React reuses these rows when answering advances to the next question — a keyboard user's focus then stays on the row instead of being dropped to <body> as the old rows unmount
              key={i}
              role={multiSelect ? 'checkbox' : 'radio'}
              aria-checked={checked}
              aria-disabled={interactionDisabled}
              tabIndex={interactionDisabled ? -1 : 0}
              className={cn(
                'flex w-full min-w-0 items-center gap-3 p-2 rounded-lg transition-colors',
                interactionDisabled ? 'cursor-default' : 'cursor-pointer',

                checked
                  ? 'bg-background'
                  : !interactionDisabled && 'hover:bg-background/40',
                isSubmitting && 'opacity-60',
              )}
              onClick={onActivate}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onActivate();
                }
              }}
              data-testid="rain-interrupt-option"
            >
              {multiSelect ? (
                <Checkbox
                  checked={checked}
                  disabled={interactionDisabled}
                  tabIndex={-1}
                  // The wrapping row handles activation via onClick. The
                  // checkbox is presentational here (pointer events pass
                  // through to the row) so a click on it doesn't toggle twice.
                  className="pointer-events-none shrink-0"
                />
              ) : (
                <span
                  aria-hidden
                  className="inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-background px-1 text-xs font-medium text-secondary-foreground"
                >
                  {i + 1}
                </span>
              )}
              <Label className="flex min-w-0 flex-1 flex-wrap items-center gap-2 break-anywhere text-sm font-normal text-card-foreground cursor-pointer">
                {displayLabel}
                {isRecommended && (
                  <Badge variant="secondary">
                    {t('rain.interrupt.recommended')}
                  </Badge>
                )}
              </Label>
            </div>
          );
        })}

        {/* The "Other" field and the actions share one row: the field flexes
            and the buttons keep their natural width, so a longer translated
            "Skip" narrows the field instead of sitting on top of what the user
            typed. pr-1 puts the buttons 16px from the card edge (12px from the
            list's px-3) to match the Figma footer inset. */}
        <Flex direction="row" align="center" gap={2} className="w-full pr-1">
          <div
            className={cn(
              'flex min-w-0 flex-1 items-center p-2 rounded-lg',
              isSubmitting && 'opacity-60',
            )}
            data-testid="rain-interrupt-option-other"
          >
            {multiSelect ? (
              <span className="mr-3 inline-flex shrink-0">
                <Checkbox
                  checked={state?.customSelected ?? false}
                  disabled={interactionDisabled}
                  onCheckedChange={() =>
                    handleToggleCustom(questionId, multiSelect)
                  }
                />
              </span>
            ) : (
              <span
                role="radio"
                aria-checked={state?.customSelected ?? false}
                aria-label={t('rain.interrupt.otherPlaceholder')}
                tabIndex={interactionDisabled ? -1 : 0}
                onClick={() => {
                  if (interactionDisabled) return;
                  handleToggleCustom(questionId, multiSelect);
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    if (!interactionDisabled)
                      handleToggleCustom(questionId, multiSelect);
                  }
                }}
                className={cn(
                  'inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-background px-1 text-xs font-medium text-secondary-foreground',
                  interactionDisabled ? 'cursor-default' : 'cursor-pointer',
                )}
              >
                {options.length + 1}
              </span>
            )}
            <Input
              value={state?.customText ?? ''}
              disabled={interactionDisabled}
              onChange={e =>
                handleCustomText(questionId, multiSelect, e.target.value)
              }
              onKeyDown={handleCustomKeyDown}
              placeholder={t('rain.interrupt.otherPlaceholder')}
              className="border-0 bg-transparent text-sm shadow-none focus-visible:ring-0 dark:bg-transparent"
              data-testid="rain-interrupt-other-input"
            />
          </div>

          <Flex direction="row" align="center" gap={2} className="shrink-0">
            <Button
              variant="outline"
              size="sm"
              onClick={skip}
              disabled={isSubmitting}
              data-testid="rain-interrupt-skip"
            >
              {t('rain.interrupt.skip')}
            </Button>
            {showForwardButton && (
              <Button
                variant={isLastQuestion ? 'default' : 'outline'}
                size="icon-sm"
                onClick={() => forward()}
                // Commits an answer, so it needs one: a ticked multi-select
                // option or typed "Other" text. Moving on *without* answering is
                // Skip's job. (Single-select never renders this disabled — the
                // button only appears once there's a typed answer.)
                disabled={!currentAnswered || isSubmitting}
                isLoading={isSubmitting && isLastQuestion}
                aria-label={
                  isLastQuestion
                    ? t('rain.interrupt.submit')
                    : t('rain.interrupt.next')
                }
                data-testid="rain-interrupt-submit"
              >
                <Icon
                  type={isLastQuestion ? 'ArrowUp' : 'ArrowRight'}
                  size="sm"
                />
              </Button>
            )}
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
}
